import React, { useState, memo } from 'react';

export type ListFieldType = 'company' | 'lastName' | 'firstName' | 'email' | 'phone' | 'source' | 'none';

interface ListCustomizerProps {
  onClose: () => void;
  onChange: (order: ListFieldType[]) => void;
  storagePrefix?: string;
  defaultOrder?: ListFieldType[];
}

const ListCustomizer = memo<ListCustomizerProps>(({ onClose, onChange, storagePrefix = '', defaultOrder }) => {
  // Default order: taskpane uses 'source' + 'none', teams uses 'phone' + 'source'
  const fallbackDefaultOrder: ListFieldType[] = defaultOrder || ['company', 'lastName', 'firstName', 'email', 'source', 'none'];
  
  const [order, setOrder] = useState<ListFieldType[]>(() => {
    try {
      const key = storagePrefix ? `${storagePrefix}.list.customOrder` : 'list.customOrder';
      const saved = localStorage.getItem(key);
      if (saved) return JSON.parse(saved) as ListFieldType[];
    } catch {}
    return fallbackDefaultOrder;
  });

  const fieldOptions: { value: ListFieldType; label: string }[] = [
    { value: 'none', label: 'None' },
    { value: 'company', label: 'Company' },
    { value: 'lastName', label: 'Last Name' },
    { value: 'firstName', label: 'First Name' },
    { value: 'email', label: 'Email' },
    { value: 'phone', label: 'Phone' },
    { value: 'source', label: 'Source' },
  ];

  const handleFieldChange = (index: number, value: ListFieldType) => {
    const isAlreadyUsed = order.some((field, idx) => idx !== index && field === value && value !== 'none');
    const newOrder = [...order];
    newOrder[index] = value;
    setOrder(newOrder);

    if (!isAlreadyUsed) {
      try {
        const key = storagePrefix ? `${storagePrefix}.list.customOrder` : 'list.customOrder';
        localStorage.setItem(key, JSON.stringify(newOrder));
      } catch (error) {
        console.error('Failed to save list order:', error);
      }
      onChange(newOrder);
    }
  };

  const handleApply = () => {
    try {
      const key = storagePrefix ? `${storagePrefix}.list.customOrder` : 'list.customOrder';
      localStorage.setItem(key, JSON.stringify(order));
    } catch (error) {
      console.error('Failed to save list settings:', error);
    }
    onChange(order);
    onClose();
  };

  const handleReset = () => {
    setOrder(fallbackDefaultOrder);
    try {
      const key = storagePrefix ? `${storagePrefix}.list.customOrder` : 'list.customOrder';
      localStorage.setItem(key, JSON.stringify(fallbackDefaultOrder));
    } catch (error) {
      console.error('Failed to save list settings:', error);
    }
    onChange(fallbackDefaultOrder);
  };

  // Ensure we have 6 dropdowns (to mirror card)
  const dropdowns = Array.from({ length: 6 }, (_, i) => i);

  return (
    <div className="absolute z-50 top-0 left-0 right-0 mx-auto max-w-sm bg-white border-2 border-blue-500 rounded-lg shadow-2xl p-4 mb-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Customize List View</h3>
        <button
          onClick={onClose}
          className="text-gray-500 hover:text-gray-700 transition-colors"
          aria-label="Close customizer"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div className="space-y-3">
        {dropdowns.map((index) => (
          <div key={index} className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700 w-20">
              Field {index + 1}:
            </label>
            <select
              value={order[index] || 'none'}
              onChange={(e) => handleFieldChange(index, e.target.value as ListFieldType)}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              aria-label={`Select field ${index + 1}`}
              title={`Select field ${index + 1}`}
            >
              {fieldOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200">
        <button
          onClick={handleReset}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
        >
          Reset to Default
        </button>
        <button
          onClick={handleApply}
          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors"
        >
          Apply
        </button>
      </div>
    </div>
  );
});

ListCustomizer.displayName = 'ListCustomizer';

export default ListCustomizer;


